class HeaderV2 extends HTMLElement {
  constructor() {
    super();
    this.setupComplete = false;
  }

  /** Shopify wraps this component in `#shopify-section-header-v2`; theme CSS targets that wrapper for sticky/layout. */
  getV2SectionRoot() {
    const w = document.getElementById('shopify-section-header-v2');
    if (w && w.contains(this)) return w;
    return this;
  }

  connectedCallback() {
    if (this.setupComplete) return;
    
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        setTimeout(() => this.setup(), 50);
      });
    } else {
      // Small delay to ensure all elements are rendered
      setTimeout(() => this.setup(), 50);
    }
  }

  setup() {
    if (this.setupComplete) return;
    this.setupMenuToggles();
    this.setupMobileMenu();
    this.setupDrilldown();
    this.setupSearchToggles();
    this.setupUserProfile();
    this.setupMegaMenuHoverPosition();
    this.setupDesktopMegamenuInteraction();
    this.setupStickyHeader();
    this.setupCart();
    this.setupMenuFontSize();
    this.setupComplete = true;
  }

  setupMegaMenuHoverPosition() {
    const update = (el) => {
      if (!el || window.innerWidth <= 1024) return;
      const link = el.querySelector('.v2-main-menu__link');
      if (!link) return;
      const rect = link.getBoundingClientRect();
      const linkBottom = Math.max(0, Math.round(rect.bottom));
      document.documentElement.style.setProperty('--v2-v2-link-bottom', `${linkBottom}px`);

      const headerGlobal = this.querySelector('.v2-header-global');
      const headerTarget = headerGlobal || this;
      const headerRect = headerTarget.getBoundingClientRect();
      let headerBottom = Math.max(0, Math.round(headerRect.bottom));
      if (headerGlobal && headerGlobal.offsetHeight > 0) {
        headerBottom = Math.max(
          headerBottom,
          Math.round(headerRect.top + headerGlobal.offsetHeight)
        );
      }
      document.documentElement.style.setProperty('--v2-header-v2-bottom', `${headerBottom}px`);
    };

    this._v2UpdateMegamenuHoverMetrics = update;

    const items = this.querySelectorAll('.v2-main-menu__item.v2-has-dropdown-menu');
    if (!items.length) return;

    items.forEach((item) => {
      item.addEventListener('mouseenter', () => update(item));
      item.addEventListener('focusin', () => update(item));
    });

    window.addEventListener('resize', () => {
      const mainNav = this.querySelector('#v2-navigation');
      const active =
        mainNav?.querySelector('.v2-main-menu__item.v2-megamenu-item-active') ||
        mainNav?.querySelector('.v2-main-menu__item.v2-has-dropdown-menu:hover') ||
        mainNav?.querySelector('.v2-main-menu__item.v2-has-dropdown-menu:focus-within');
      if (active) update(active);
    });
  }

  setupDesktopMegamenuInteraction() {
    let syncMegamenuForCartOpened = () => {};
    let syncMegamenuForCartClosed = () => {};

    window.__v2ReleaseMegamenuForCartDrawer = () => {};

    document.addEventListener('cart-drawer:opened', () => syncMegamenuForCartOpened());
    document.addEventListener('cart-drawer:contents-updated', () => syncMegamenuForCartOpened());
    document.addEventListener('cart-drawer:closed', () => syncMegamenuForCartClosed());

    const mainNav = this.querySelector('#v2-navigation');
    if (!mainNav) return;

    const isDesktop = () => window.innerWidth > 1024;
    const mobileNavOpen = () =>
      window.innerWidth <= 1024 &&
      !!document.querySelector('.v2-mobile-navigation.v2-show-mobile-nav');
    const isCartDrawerOpen = () => {
      const cd = document.querySelector('cart-drawer');
      if (!cd || !cd.classList.contains('drawer')) return false;
      if (cd.hasAttribute('data-v2-cart-drawer-closing')) return true;
      return cd.classList.contains('active');
    };
    const reducedMotion = () =>
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let locked = false;
    let lockedGutter = null;
    let stickyObserver = null;

    const getScrollbarWidth = () =>
      Math.max(0, window.innerWidth - document.documentElement.clientWidth);

    const syncStickyHeaderPadding = () => {
      const headerGlobal = this.querySelector('.v2-header-global');
      if (!headerGlobal || !locked) return;
      const sw = lockedGutter ?? 0;
      const root = this.getV2SectionRoot();
      if (root.classList.contains('sticky') && sw > 0) {
        headerGlobal.style.paddingRight = `${sw}px`;
      } else {
        headerGlobal.style.paddingRight = '';
      }
    };

    const clearMegamenuScrollLock = () => {
      locked = false;
      lockedGutter = null;
      if (stickyObserver) {
        stickyObserver.disconnect();
        stickyObserver = null;
      }
      document.body.removeAttribute('data-v2-megamenu-scroll-lock');
      document.documentElement.style.removeProperty('--v2-scrollbar-compensation');
      document.documentElement.style.removeProperty('overflow');
      document.body.style.paddingRight = '';
      const headerGlobal = this.querySelector('.v2-header-global');
      if (headerGlobal) headerGlobal.style.paddingRight = '';
      if (mobileNavOpen()) {
        document.body.style.setProperty('overflow', 'hidden');
      } else {
        document.body.style.removeProperty('overflow');
      }
    };

    const unlock = () => {
      if (!locked) return;
      clearMegamenuScrollLock();
    };

    const lock = () => {
      if (!isDesktop()) return;
      if (isCartDrawerOpen()) return;
      if (locked && !document.body.hasAttribute('data-v2-megamenu-scroll-lock')) {
        if (stickyObserver) {
          stickyObserver.disconnect();
          stickyObserver = null;
        }
        locked = false;
        lockedGutter = null;
      }
      if (!locked) {
        lockedGutter = getScrollbarWidth();
        locked = true;
        document.body.setAttribute('data-v2-megamenu-scroll-lock', '');
        document.documentElement.style.setProperty(
          '--v2-scrollbar-compensation',
          lockedGutter > 0 ? `${lockedGutter}px` : '0px'
        );
        document.body.style.overflow = 'hidden';
        if (lockedGutter > 0) {
          document.body.style.paddingRight = `${lockedGutter}px`;
        }
        if (!stickyObserver) {
          stickyObserver = new MutationObserver(() => syncStickyHeaderPadding());
          stickyObserver.observe(this.getV2SectionRoot(), {
            attributes: true,
            attributeFilter: ['class'],
          });
        }
      }
      syncStickyHeaderPadding();
      requestAnimationFrame(() => {
        const li = mainNav.querySelector('.v2-main-menu__item.v2-megamenu-item-active');
        this._v2UpdateMegamenuHoverMetrics?.(li);
      });
    };

    const resetMegamenuUi = () => {
      mainNav.classList.remove('v2-megamenu-tween', 'v2-megamenu-js-open');
      mainNav.querySelectorAll('.v2-megamenu-item-active').forEach((el) => {
        el.classList.remove('v2-megamenu-item-active');
      });
    };

    window.__v2ReleaseMegamenuForCartDrawer = () => {
      resetMegamenuUi();
      clearMegamenuScrollLock();
    };
    syncMegamenuForCartOpened = () => {
      window.__v2ReleaseMegamenuForCartDrawer();
    };
    syncMegamenuForCartClosed = () => {
      clearMegamenuScrollLock();
    };

    const armMaxHeightTweenEnd = (wrapper, onDone) => {
      const finish = () => {
        mainNav.classList.remove('v2-megamenu-tween');
        onDone?.();
      };
      if (!wrapper || reducedMotion()) {
        finish();
        return;
      }
      const onEnd = (ev) => {
        if (ev.target !== wrapper || ev.propertyName !== 'max-height') return;
        wrapper.removeEventListener('transitionend', onEnd);
        window.clearTimeout(fallback);
        finish();
      };
      const fallback = window.setTimeout(() => {
        wrapper.removeEventListener('transitionend', onEnd);
        finish();
      }, 450);
      wrapper.addEventListener('transitionend', onEnd);
    };

    const openDropdownItem = (item) => {
      if (!isDesktop() || !item.classList.contains('v2-has-dropdown-menu')) return;
      document.dispatchEvent(new CustomEvent('header-v2:menu-open', {
        detail: { source: mainNav },
      }));
      document.querySelectorAll('predictive-search[open]').forEach((search) => {
        if (typeof search.close === 'function') search.close();
      });
      const hadOpen = mainNav.classList.contains('v2-megamenu-js-open');
      mainNav
        .querySelectorAll('.v2-megamenu-item-active')
        .forEach((el) => el.classList.remove('v2-megamenu-item-active'));
      item.classList.add('v2-megamenu-item-active');

      if (!hadOpen) {
        mainNav.classList.add('v2-megamenu-tween', 'v2-megamenu-js-open');
        lock();
        const wrapper = item.querySelector('.v2-megamenu-wrapper');
        requestAnimationFrame(() => {
          this._v2UpdateMegamenuHoverMetrics?.(item);
        });
        armMaxHeightTweenEnd(wrapper, () => {});
      } else {
        mainNav.classList.remove('v2-megamenu-tween');
        lock();
        requestAnimationFrame(() => {
          this._v2UpdateMegamenuHoverMetrics?.(item);
        });
      }
    };

    const closeMegamenu = () => {
      if (!mainNav.classList.contains('v2-megamenu-js-open')) {
        if (locked && !mainNav.classList.contains('v2-megamenu-tween')) {
          clearMegamenuScrollLock();
        }
        return;
      }
      const prevActive = mainNav.querySelector('.v2-megamenu-item-active');
      const wrapper = prevActive?.querySelector('.v2-megamenu-wrapper');

      mainNav.classList.add('v2-megamenu-tween');
      mainNav.classList.remove('v2-megamenu-js-open');
      mainNav
        .querySelectorAll('.v2-megamenu-item-active')
        .forEach((el) => el.classList.remove('v2-megamenu-item-active'));

      armMaxHeightTweenEnd(wrapper, () => {
        unlock();
      });
    };

    document.addEventListener('predictive-search:open', closeMegamenu);
    document.addEventListener('header-v2:search-open', closeMegamenu);

    mainNav.querySelectorAll('.v2-main-menu.v2-level-1 > .v2-main-menu__item').forEach((item) => {
      item.addEventListener('mouseenter', () => {
        if (!isDesktop()) return;
        if (item.classList.contains('v2-has-dropdown-menu')) {
          openDropdownItem(item);
        } else {
          closeMegamenu();
        }
      });
    });

    mainNav.addEventListener('mouseleave', (e) => {
      if (!isDesktop()) return;
      if (e.relatedTarget && mainNav.contains(e.relatedTarget)) return;
      closeMegamenu();
    });

    mainNav.addEventListener('focusin', (e) => {
      const item = e.target.closest('.v2-main-menu__item');
      if (!item || !mainNav.contains(item)) return;
      if (!isDesktop()) return;
      if (item.classList.contains('v2-has-dropdown-menu')) {
        openDropdownItem(item);
      } else {
        closeMegamenu();
      }
    });

    mainNav.addEventListener('focusout', (e) => {
      if (!isDesktop()) return;
      if (e.relatedTarget && mainNav.contains(e.relatedTarget)) return;
      closeMegamenu();
    });

    window.addEventListener('resize', () => {
      if (!isDesktop()) {
        resetMegamenuUi();
        unlock();
        return;
      }
      if (locked) {
        syncStickyHeaderPadding();
        const li = mainNav.querySelector('.v2-main-menu__item.v2-megamenu-item-active');
        this._v2UpdateMegamenuHoverMetrics?.(li);
      }
    });
  }

  setupMenuToggles() {
    const toggles = this.querySelectorAll('.v2-menu-item-toggle');
    toggles.forEach(toggle => {
      toggle.addEventListener('click', () => {
        if (window.innerWidth <= 1024) {
          const level2 = toggle.nextElementSibling;
          if (level2 && level2.classList.contains('v2-second-level')) {
            const arrowDown = toggle.querySelector('.arrow-down-svg');
            const arrowUp = toggle.querySelector('.arrow-up-svg');
            if (arrowDown) arrowDown.classList.toggle('hide');
            if (arrowUp) arrowUp.classList.toggle('hide');
            const displayValue = level2.classList.contains('v2-brands-grid') ? 'flex' : 'block';
            const isOpen = level2.style.display && level2.style.display !== 'none';
            level2.style.display = isOpen ? '' : displayValue;
          }
        }
      });
    });
  }

  setupDrilldown() {
    const drilldown = this.querySelector('.v2-drilldown');
    if (!drilldown) return;

    const level1 = drilldown.querySelector('.v2-drilldown-level-1');
    const panels = drilldown.querySelectorAll('.v2-drilldown-panel');
    const triggers = drilldown.querySelectorAll('.v2-drilldown-trigger');
    const backButtons = drilldown.querySelectorAll('.v2-drilldown-back');
    const navigation = document.querySelector('.v2-mobile-navigation');
    const DURATION_MS = 280;

    const clearAnimClasses = (el) => {
      if (!el) return;
      el.classList.remove('v2-drilldown-active', 'v2-drilldown-behind', 'v2-drilldown-exit-left', 'v2-drilldown-exit-right', 'v2-drilldown-enter-left', 'v2-drilldown-enter-right');
    };

    const showLevel = (el) => {
      if (!el) return;
      el.removeAttribute('hidden');
      clearAnimClasses(el);
      el.classList.add('v2-drilldown-active');
    };

    const hideLevel = (el) => {
      if (!el) return;
      clearAnimClasses(el);
      el.setAttribute('hidden', '');
    };

    const openPanel = (panel) => {
      if (!panel || !level1) return;
      const activePanel = drilldown.querySelector('.v2-drilldown-panel.v2-drilldown-active');
      if (activePanel) {
        activePanel.classList.remove('v2-drilldown-active');
        activePanel.setAttribute('hidden', '');
      }
      panel.removeAttribute('hidden');
      level1.classList.add('v2-drilldown-behind', 'v2-drilldown-exit-left');
      panel.classList.add('v2-drilldown-enter-right');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          panel.classList.remove('v2-drilldown-enter-right');
          panel.classList.add('v2-drilldown-active');
        });
      });
      setTimeout(() => {
        level1.classList.remove('v2-drilldown-behind', 'v2-drilldown-exit-left');
        level1.setAttribute('hidden', '');
      }, DURATION_MS);
    };

    const closePanel = (panel, parentId) => {
      if (!panel || !level1) return;
      level1.removeAttribute('hidden');
      level1.classList.add('v2-drilldown-enter-left');
      panel.classList.add('v2-drilldown-exit-right', 'v2-drilldown-behind');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          level1.classList.remove('v2-drilldown-enter-left');
          level1.classList.add('v2-drilldown-active');
        });
      });
      setTimeout(() => {
        panel.classList.remove('v2-drilldown-active', 'v2-drilldown-exit-right', 'v2-drilldown-behind');
        panel.setAttribute('hidden', '');
        level1.classList.remove('v2-drilldown-behind');
        if (parentId !== '1') {
          const parentPanel = document.getElementById(parentId);
          if (parentPanel) showLevel(parentPanel);
        }
      }, DURATION_MS);
    };

    const resetToLevel1 = () => {
      panels.forEach((p) => {
        p.classList.remove('v2-drilldown-active', 'v2-drilldown-behind', 'v2-drilldown-exit-left', 'v2-drilldown-exit-right', 'v2-drilldown-enter-left', 'v2-drilldown-enter-right');
        p.setAttribute('hidden', '');
      });
      if (level1) {
        level1.classList.remove('v2-drilldown-behind', 'v2-drilldown-exit-left', 'v2-drilldown-exit-right', 'v2-drilldown-enter-left', 'v2-drilldown-enter-right');
        level1.removeAttribute('hidden');
        level1.classList.add('v2-drilldown-active');
      }
    };

    triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const panelId = trigger.getAttribute('data-drill-panel');
        if (!panelId) return;
        const panel = document.getElementById(panelId);
        if (!panel) return;
        openPanel(panel);
      });
    });

    backButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const panel = btn.closest('.v2-drilldown-panel');
        if (!panel) return;
        const parentId = panel.getAttribute('data-drill-parent');
        closePanel(panel, parentId);
      });
    });

    if (navigation) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((m) => {
          if (m.attributeName === 'class') {
            if (!navigation.classList.contains('v2-show-mobile-nav')) {
              resetToLevel1();
            }
          }
        });
      });
      observer.observe(navigation, { attributes: true });
    }

    const DROPDOWN_DURATION_MS = 440;
    drilldown.querySelectorAll('.v2-dropdown-toggle:not([data-dropdown-nested])').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (window.innerWidth > 1024) return;
        const section = btn.closest('.v2-dropdown-section');
        const contentId = btn.getAttribute('aria-controls');
        const content = contentId ? document.getElementById(contentId) : null;
        const isOpen = btn.getAttribute('aria-expanded') === 'true';
        if (content) {
          if (isOpen) {
            btn.setAttribute('aria-expanded', 'false');
            content.setAttribute('aria-hidden', 'true');
            section?.classList.remove('v2-dropdown-section--open');
            setTimeout(() => {
              content.setAttribute('hidden', '');
            }, DROPDOWN_DURATION_MS);
          } else {
            content.removeAttribute('hidden');
            content.setAttribute('aria-hidden', 'false');
            btn.setAttribute('aria-expanded', 'true');
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                section?.classList.add('v2-dropdown-section--open');
              });
            });
          }
        }
      });
    });

    drilldown.querySelectorAll('.v2-dropdown-toggle[data-dropdown-nested]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (window.innerWidth > 1024) return;
        const nestedList = btn.nextElementSibling;
        const isOpen = btn.getAttribute('aria-expanded') === 'true';
        if (nestedList && nestedList.classList.contains('v2-dropdown-nested-list')) {
          nestedList.classList.toggle('v2-dropdown-nested--open', !isOpen);
          btn.setAttribute('aria-expanded', !isOpen);
        }
      });
    });
  }

  setupMobileMenu() {
    const menuToggle = this.querySelector('.v2-menu-toggle');
    const menuClose = this.querySelector('.v2-close-menu');
    const navigation = document.querySelector('.v2-mobile-navigation');
    const headerBannerWrapper = document.querySelector('.v2-header-banner--wrapper');

    if (menuToggle && menuClose && navigation) {
      const openMenu = () => {
        document.dispatchEvent(new CustomEvent('header-v2:menu-open', {
          detail: { source: navigation },
        }));
        navigation.classList.add('v2-show-mobile-nav');
        menuToggle.classList.add('is-open');
        menuToggle.setAttribute('aria-expanded', 'true');
        if (headerBannerWrapper) {
          headerBannerWrapper.style.zIndex = 1;
        }
        menuClose.focus();
        document.body.style.overflow = 'hidden';
      };

      const closeMenu = (returnFocus = true) => {
        navigation.classList.remove('v2-show-mobile-nav');
        menuToggle.classList.remove('is-open');
        menuToggle.setAttribute('aria-expanded', 'false');
        if (headerBannerWrapper) {
          headerBannerWrapper.style.zIndex = 30;
        }
        if (returnFocus) menuToggle.focus();
        document.body.style.removeProperty('overflow');
      };

      menuToggle.addEventListener('click', () => {
        if (navigation.classList.contains('v2-show-mobile-nav')) {
          closeMenu();
        } else {
          openMenu();
        }
      });

      menuClose.addEventListener('click', closeMenu);
      document.addEventListener('header-v2:search-open', () => closeMenu(false));

      window.addEventListener('resize', () => {
        if (window.innerWidth > 1024 && navigation.classList.contains('v2-show-mobile-nav')) {
          closeMenu(false);
        }
      });
    }

    // Third level menu toggles
    const iconPlusElements = this.querySelectorAll('.icon-plus');
    iconPlusElements.forEach(iconPlus => {
      const parentMenu = iconPlus.closest('.v2-mobile-navigation');
      if (parentMenu) {
        iconPlus.addEventListener('click', () => {
          const level3 = iconPlus.parentElement.nextElementSibling;
          const parentLink = iconPlus.parentElement.previousElementSibling;
          if (level3 && level3.classList.contains('v2-third-level')) {
            parentLink.classList.toggle('bold');
            level3.classList.toggle('show');
            iconPlus.classList.toggle('hide');
            const iconMinus = iconPlus.nextElementSibling;
            if (iconMinus) iconMinus.classList.toggle('hide');
          }
        });
      }
    });

    const iconMinusElements = this.querySelectorAll('.icon-minus');
    iconMinusElements.forEach(iconMinus => {
      const parentMenu = iconMinus.closest('.v2-mobile-navigation');
      if (parentMenu) {
        iconMinus.addEventListener('click', () => {
          const level3 = iconMinus.parentElement.nextElementSibling;
          const parentLink = iconMinus.parentElement.previousElementSibling;
          if (level3 && level3.classList.contains('v2-third-level')) {
            parentLink.classList.toggle('bold');
            level3.classList.toggle('show');
            iconMinus.classList.toggle('hide');
            const iconPlus = iconMinus.previousElementSibling;
            if (iconPlus) iconPlus.classList.toggle('hide');
          }
        });
      }
    });
  }

  setupSearchToggles() {
    const searchToggleBtn = this.querySelector('.v2-mobile-search-wrapper .v2-search-toggle, .v2-search-toggle.large-up--hide');
    const mobileSearch = document.querySelector('.v2-mobile-search');
    const mobileSearchInput = document.getElementById('v2-Search-In-Modal-Mobile');
    const closePredictiveSearches = () => {
      document.querySelectorAll('predictive-search[open]').forEach((search) => {
        if (typeof search.close === 'function') search.close();
      });
    };
    const focusWithoutScroll = (input) => {
      if (!input) return;
      try {
        input.focus({ preventScroll: true });
      } catch (error) {
        input.focus();
      }
    };

    if (searchToggleBtn && mobileSearch) {
      searchToggleBtn.addEventListener('click', (event) => {
        event.preventDefault();
        mobileSearch.classList.toggle('v2-mobile-search-active');
        if (mobileSearch.classList.contains('v2-mobile-search-active')) {
          document.dispatchEvent(new CustomEvent('header-v2:search-open', {
            detail: { source: mobileSearch },
          }));
          mobileSearch.style.maxHeight = mobileSearch.scrollHeight + 'px';
          focusWithoutScroll(mobileSearchInput);
        } else {
          mobileSearch.style.maxHeight = 0;
          closePredictiveSearches();
        }
      });
    }

    const desktopSearchToggleBtn = this.querySelector('.v2-desktop-search-toggle');
    const desktopSearch = document.querySelector('.v2-desktop-search');
    const desktopSearchInput = document.getElementById('v2-Search-In-Modal-Desktop');

    if (desktopSearchToggleBtn && desktopSearch) {
      desktopSearchToggleBtn.addEventListener('click', (event) => {
        event.preventDefault();
        desktopSearch.classList.toggle('v2-desktop-search-active');
        if (desktopSearch.classList.contains('v2-desktop-search-active')) {
          document.dispatchEvent(new CustomEvent('header-v2:search-open', {
            detail: { source: desktopSearch },
          }));
          if (desktopSearchInput) {
            setTimeout(() => {
              focusWithoutScroll(desktopSearchInput);
            }, 100);
          }
        } else {
          closePredictiveSearches();
        }
      });

      document.addEventListener('click', (event) => {
        if (desktopSearch && desktopSearchToggleBtn && 
            !desktopSearch.contains(event.target) && 
            !desktopSearchToggleBtn.contains(event.target)) {
          desktopSearch.classList.remove('v2-desktop-search-active');
          closePredictiveSearches();
        }
      });
    }

    document.addEventListener('header-v2:menu-open', () => {
      if (mobileSearch) {
        mobileSearch.classList.remove('v2-mobile-search-active');
        mobileSearch.style.maxHeight = 0;
      }
      if (desktopSearch) desktopSearch.classList.remove('v2-desktop-search-active');
      closePredictiveSearches();
    });
  }

  setupUserProfile() {
    const userProfileButtons = this.querySelectorAll('.v2-user-profile-button');
    
    userProfileButtons.forEach(userProfileButton => {
      const panelId = userProfileButton.getAttribute('aria-controls');
      const userPanel = document.getElementById(panelId);
      
      if (userProfileButton && userPanel) {
        const userFirstLink = userPanel.querySelectorAll('a')[0];

        const handleClick = (e) => {
          e.stopPropagation();
          const isActive = userPanel.classList.contains('v2-user-panel-active');
          
          if (isActive) {
            // Close panel
            userPanel.classList.remove('v2-user-panel-active');
            userPanel.setAttribute('aria-expanded','false');
            userProfileButton.setAttribute('aria-expanded','false');
            // Clear all inline styles
            userPanel.style.cssText = '';
          } else {
            // Open panel
            userPanel.classList.add('v2-user-panel-active');
            userPanel.setAttribute('aria-expanded','true');
            userProfileButton.setAttribute('aria-expanded','true');
            
            // For mobile, apply fixed positioning
            if (window.innerWidth <= 1024) {
              const buttonRect = userProfileButton.getBoundingClientRect();
              userPanel.style.cssText = `
                position: fixed !important;
                top: ${buttonRect.bottom + 10}px !important;
                right: ${window.innerWidth - buttonRect.right}px !important;
                left: auto !important;
                opacity: 1 !important;
                visibility: visible !important;
                display: block !important;
                margin-top: 0 !important;
                z-index: 1001 !important;
              `;
            }
            
            if (userFirstLink) {
              setTimeout(() => userFirstLink.focus(), 100);
            }
          }
        };

        userProfileButton.addEventListener('click', handleClick);
        userProfileButton.addEventListener('touchend', (e) => {
          e.preventDefault();
          handleClick(e);
        }, { passive: false });

        // Close on outside click
        const handleOutsideClick = (event) => {
          if (userPanel && userProfileButton && 
              !userPanel.contains(event.target) && 
              !userProfileButton.contains(event.target) &&
              userPanel.classList.contains('v2-user-panel-active')) {
            userPanel.classList.remove('v2-user-panel-active');
            userPanel.setAttribute('aria-expanded','false');
            userProfileButton.setAttribute('aria-expanded','false');
            userPanel.style.cssText = '';
          }
        };

        document.addEventListener('click', handleOutsideClick);
        document.addEventListener('touchend', handleOutsideClick);

        // Update position on scroll/resize for mobile
        const updatePosition = () => {
          if (window.innerWidth <= 1024 && userPanel.classList.contains('v2-user-panel-active')) {
            const buttonRect = userProfileButton.getBoundingClientRect();
            userPanel.style.setProperty('top', `${buttonRect.bottom + 10}px`, 'important');
            userPanel.style.setProperty('right', `${window.innerWidth - buttonRect.right}px`, 'important');
          }
        };

        window.addEventListener('scroll', updatePosition, { passive: true });
        window.addEventListener('resize', () => {
          if (window.innerWidth > 1024 && userPanel.classList.contains('v2-user-panel-active')) {
            userPanel.style.cssText = '';
          } else {
            updatePosition();
          }
        });
      }
    });
  }

  setupStickyHeader() {
    const v2YnHeader = this.getV2SectionRoot();
    const v2YnMainContent = document.getElementById("MainContent");

    if (!v2YnHeader) return;

    const headerGlobal = v2YnHeader.querySelector(".v2-header-global");
    let stickyThreshold = headerGlobal ? headerGlobal.offsetTop : v2YnHeader.offsetTop;
    let lastIsSticky = v2YnHeader.classList.contains("sticky");
    let lastPaddingTop = '';
    const stickyHysteresis = 2;
    let lastModeIsMobile = window.innerWidth <= 1024;
    let lastMobileFixed = false;
    let lastMobilePlaceholderHeight = '';

    const updateMegamenuTop = () => {
      const target = headerGlobal || v2YnHeader;
      const rect = target.getBoundingClientRect();
      const bottom = Math.max(0, Math.round(rect.bottom));
      document.documentElement.style.setProperty('--v2-header-v2-bottom', `${bottom}px`);
    };

    function getAnnouncementVisibleOffset() {
      const bar = document.querySelector('[data-announcement-bar]');
      if (!bar) return 0;
      const rect = bar.getBoundingClientRect();
      const height = rect.height || 0;
      const visible = Math.max(0, Math.min(height, rect.bottom));
      return Math.round(visible);
    }

    const v2StickyHeader = () => {
      if (!v2YnHeader) return;
      
      const headerBanner = v2YnHeader.querySelector(".v2-header-banner");
      const isMobile = window.innerWidth <= 1024;

      if (isMobile) {
        const announcementVisible = getAnnouncementVisibleOffset();
        const shouldUseMobileFixed = announcementVisible <= 0;

        if (!lastModeIsMobile && v2YnHeader.classList.contains("sticky")) {
          v2YnHeader.classList.remove("sticky");
          lastIsSticky = false;
        }

        if (v2YnHeader.style.top) v2YnHeader.style.top = "";
        if (headerGlobal && headerGlobal.style.top) headerGlobal.style.top = "";
        if (v2YnMainContent && v2YnMainContent.style.paddingTop) {
          v2YnMainContent.style.paddingTop = "";
          lastPaddingTop = "";
        }

        if (shouldUseMobileFixed !== lastMobileFixed) {
          v2YnHeader.classList.toggle('v2-mobile-fixed', shouldUseMobileFixed);
          lastMobileFixed = shouldUseMobileFixed;
        }

        if (shouldUseMobileFixed && headerGlobal) {
          const h = headerGlobal.offsetHeight || 0;
          const nextHeight = h > 0 ? `${h}px` : '';
          if (v2YnHeader.style.height !== nextHeight) v2YnHeader.style.height = nextHeight;
          lastMobilePlaceholderHeight = nextHeight;
        } else if (!shouldUseMobileFixed) {
          if (v2YnHeader.style.height) v2YnHeader.style.height = '';
          lastMobilePlaceholderHeight = '';
        }

        // Keep CSS var in sync on mobile too so the mobile menu can sit below the header
        updateMegamenuTop();

        lastModeIsMobile = true;
        return;
      }

      if (lastModeIsMobile) {
        stickyThreshold = headerGlobal ? (headerGlobal.offsetTop || stickyThreshold) : (v2YnHeader.offsetTop || stickyThreshold);
        if (lastMobileFixed) {
          v2YnHeader.classList.remove('v2-mobile-fixed');
          lastMobileFixed = false;
        }
        if (lastMobilePlaceholderHeight || v2YnHeader.style.height) {
          v2YnHeader.style.height = '';
          lastMobilePlaceholderHeight = '';
        }
      }
      lastModeIsMobile = false;

      if (headerBanner) {
        headerBanner.classList.remove('hide');
      }

      const announcementOffset = getAnnouncementVisibleOffset();
      
      const updateMainContentPadding = (isSticky, includeAnnouncementOffset = false) => {
        if (!v2YnMainContent || !headerGlobal) return;
        let nextPadding = "0px";
        if (isSticky) {
          const headerHeight = headerGlobal.offsetHeight || 0;
          const offset = includeAnnouncementOffset ? announcementOffset : 0;
          nextPadding = (headerHeight + offset) + "px";
        }

        const currentPadding = parseFloat(v2YnMainContent.style.paddingTop) || 0;
        const nextPaddingNum = parseFloat(nextPadding) || 0;
        if (Math.abs(currentPadding - nextPaddingNum) > 0.1 || nextPadding !== lastPaddingTop) {
          v2YnMainContent.style.paddingTop = nextPadding;
          lastPaddingTop = nextPadding;
        }
      };
      
      if (v2YnHeader.style.top) v2YnHeader.style.top = "";

        if (!v2YnHeader.classList.contains("sticky")) {
          stickyThreshold = headerGlobal ? (headerGlobal.offsetTop || stickyThreshold) : (v2YnHeader.offsetTop || stickyThreshold);
        }
        const scrollTop = window.pageYOffset || window.scrollY || 0;
        
        if (scrollTop >= (stickyThreshold + stickyHysteresis)) {
          if (!lastIsSticky) {
            v2YnHeader.classList.add("sticky");
            lastIsSticky = true;
          }
        } else if (scrollTop <= (stickyThreshold - stickyHysteresis)) {
          if (lastIsSticky) {
            v2YnHeader.classList.remove("sticky");
            lastIsSticky = false;
          }
        }

        updateMainContentPadding(lastIsSticky, lastIsSticky);

        if (headerGlobal) {
          headerGlobal.style.top = lastIsSticky ? (announcementOffset + "px") : "";
        }

      updateMegamenuTop();
    };

    let stickyTicking = false;
    const onStickyScroll = () => {
      if (stickyTicking) return;
      stickyTicking = true;
      requestAnimationFrame(() => {
        stickyTicking = false;
        v2StickyHeader();
      });
    };

    window.addEventListener('scroll', onStickyScroll, { passive: true });
    window.addEventListener('resize', v2StickyHeader);
    
    v2StickyHeader();
    setTimeout(v2StickyHeader, 100);
  }

  setupCart() {
    const cartButtons = document.querySelectorAll('.v2-cart-button, .v2-cart-button-desktop, .v2-cart-button-mobile, .v2-mobile-cart-wrapper .v2-cart-button');

    if (!cartButtons.length) return;

    cartButtons.forEach(cartButton => {
      cartButton.addEventListener('click', () => {
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          cartDrawer.open();
        }
      });
    });

    const updateCartQuantity = () => {
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          const itemCountElements = document.querySelectorAll('.v2-item-count');
          const minicartQuantityBadges = document.querySelectorAll('.v2-minicart-quantity');
          const isMobile = window.innerWidth <= 1024;
          const topBannerWrapper = document.querySelector('.v2-top-banner-wrapper');
          
          itemCountElements.forEach(element => {
            element.textContent = cart.item_count;
          });
          
          minicartQuantityBadges.forEach(badge => {
            if (cart.item_count > 0) {
              badge.style.display = 'flex';
              badge.removeAttribute('data-empty');
            } else {
              badge.style.display = 'none';
              badge.setAttribute('data-empty', 'true');
            }
          });
          
          // Mobile: adjust padding-right based on cart state
          if (isMobile && topBannerWrapper) {
            if (cart.item_count === 0) {
              topBannerWrapper.style.paddingRight = '0';
            } else {
              topBannerWrapper.style.paddingRight = '';
            }
          }
        })
        .catch(error => {
          console.error('Error updating cart quantity:', error);
        });
    };

    updateCartQuantity();

    const originalFetch = window.fetch;
    window.fetch = function(...args) {
      const url = args[0];
      const isCartUpdate = typeof url === 'string' && (
        url.includes('/cart/add.js') || 
        url.includes('/cart/change.js') || 
        url.includes('/cart/update.js') ||
        url.includes('/cart/clear.js')
      );

      if (isCartUpdate) {
        return originalFetch.apply(this, args).then(response => {
          // Update immediately for instant feedback, especially when adding products
          updateCartQuantity();
          // Also update after a short delay to ensure server state is synced
          setTimeout(() => {
            updateCartQuantity();
          }, 50);
          return response;
        }).catch(error => {
          console.error('Error in cart fetch:', error);
          return Promise.reject(error);
        });
      }
      
      return originalFetch.apply(this, args);
    };

    document.addEventListener('cart:updated', updateCartQuantity);
    
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      cartDrawer.addEventListener('cart:updated', updateCartQuantity);
    }

    window.addEventListener('cart:changed', updateCartQuantity);

    const subscribeToCartEvents = () => {
      const checkForSubscribe = () => {
        const subscribeFn = window.subscribe || (typeof subscribe !== 'undefined' ? subscribe : null);
        const pubSubEvents = window.PUB_SUB_EVENTS || (typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null);
        
        if (pubSubEvents && subscribeFn) {
          subscribeFn(pubSubEvents.cartUpdate, (event) => {
            if (event && event.cartData && event.cartData.item_count !== undefined) {
              const itemCountElements = document.querySelectorAll('.v2-item-count');
              const minicartQuantityBadges = document.querySelectorAll('.v2-minicart-quantity');
              const isMobile = window.innerWidth <= 1024;
              const topBannerWrapper = document.querySelector('.v2-top-banner-wrapper');
              
              itemCountElements.forEach(element => {
                element.textContent = event.cartData.item_count;
              });
              
              minicartQuantityBadges.forEach(badge => {
                if (event.cartData.item_count > 0) {
                  badge.style.display = 'flex';
                  badge.removeAttribute('data-empty');
                } else {
                  badge.style.display = 'none';
                  badge.setAttribute('data-empty', 'true');
                }
              });
              
              // Mobile: adjust padding-right based on cart state
              if (isMobile && topBannerWrapper) {
                if (event.cartData.item_count === 0) {
                  topBannerWrapper.style.paddingRight = '0';
                } else {
                  topBannerWrapper.style.paddingRight = '';
                }
              }
            } else {
              updateCartQuantity();
            }
          });
          return true;
        }
        return false;
      };
      
      if (!checkForSubscribe()) {
        let attempts = 0;
        const maxAttempts = 50;
        const interval = setInterval(() => {
          attempts++;
          if (checkForSubscribe() || attempts >= maxAttempts) {
            clearInterval(interval);
          }
        }, 100);
      }
    };
    
    setTimeout(subscribeToCartEvents, 200);

    document.addEventListener('shopify:section:load', updateCartQuantity);
    document.addEventListener('shopify:section:reorder', updateCartQuantity);
    
    // Handle resize to adjust padding when switching between mobile/desktop
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        updateCartQuantity();
      }, 100);
    });
  }

  setupMenuFontSize() {
    const adjustMenuFontSize = () => {
      if (window.innerWidth < 1025) {
        const menuItems = document.querySelectorAll('.v2-main-menu__link');
        menuItems.forEach(item => {
          item.style.fontSize = '';
        });
        return;
      }
      
      const menu = document.querySelector('.v2-main-menu');
      const menuItems = document.querySelectorAll('.v2-main-menu__link');
      
      if (!menu || !menuItems.length) return;
      
      const menuContainer = menu.parentElement;
      const containerWidth = menuContainer.offsetWidth;
      const minFontSize = 11;
      const maxFontSize = 16;
      
      menuItems.forEach(item => {
        item.style.fontSize = maxFontSize + 'px';
      });
      
      let menuWidth = menu.scrollWidth;
      
      if (menuWidth > containerWidth) {
        let fontSize = maxFontSize;
        let iterations = 0;
        const maxIterations = 30;
        
        while (menuWidth > containerWidth && fontSize > minFontSize && iterations < maxIterations) {
          fontSize -= 0.5;
          menuItems.forEach(item => {
            item.style.fontSize = fontSize + 'px';
          });
          
          menuWidth = menu.scrollWidth;
          if (menuWidth <= containerWidth) break;
          
          iterations++;
        }
      }
    };
    
    adjustMenuFontSize();
    
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(adjustMenuFontSize, 150);
    });
    
    setTimeout(adjustMenuFontSize, 300);
  }
}

customElements.define('header-v2', HeaderV2);
